import { format, isSameMonth, parseISO, isWithinInterval } from "date-fns";
import {
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
} from "date-fns";

import { Storage } from "../../components/services/storage/storage";
import { DandvatsHistoryModel, DandvatsModel, SaveDandvatsModel } from "../models/DandvatsModel";
import { ResponseModel } from "../models/ResponseModel";
import { AjaxService } from "../shared/services/AjaxService";

export interface DandvatEntry {
    id: string;
    userId: string;
    date: string;
    count: number;
    marks: number;
}

const STORAGE_KEY = "iskcon_app_dandvats";

async function safeParseJson<T>(raw: string | null, fallback: T): Promise<T> {
    try {
        if (!raw) return fallback;
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

export type HistoryFilter = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";

export const DandvatService = {

    async getAgainstDate(payload: DandvatsModel): Promise<ResponseModel> {
        return AjaxService.post<ResponseModel>("/Dandvats/getDandvatAgainstDate", payload);
    },

    async saveDandvat(payload: SaveDandvatsModel): Promise<ResponseModel> {
        return AjaxService.post<ResponseModel>("/Dandvats/create", payload);
    },

    async getDandvatHistory(payload: DandvatsHistoryModel): Promise<ResponseModel> {
        return AjaxService.post<ResponseModel>("/Dandvats/getDandvatHistory", payload);
    },

    async getDandvat(dandvatId: string): Promise<ResponseModel> {
        return AjaxService.get<ResponseModel>(`/Dandvats/getDandvat?dandvatId=${encodeURIComponent(dandvatId)}`);
    },

    async deleteDandvat(dandvatId: string): Promise<ResponseModel> {
        return AjaxService.delete<ResponseModel>(`/Dandvats/delete?dandvatId=${encodeURIComponent(dandvatId)}`);
    },

    async _getRawEntries(): Promise<DandvatEntry[]> {
        const raw = await Storage.get(STORAGE_KEY);
        return safeParseJson<DandvatEntry[]>(raw, []);
    },

    async _saveRawEntries(entries: DandvatEntry[]) {
        await Storage.set(STORAGE_KEY, JSON.stringify(entries));
    },

    async getAllEntries(userId: string): Promise<DandvatEntry[]> {
        const all = await DandvatService._getRawEntries();
        return all.filter((e) => e.userId === userId);
    },

    async getEntryByDate(userId: string, dateStr: string): Promise<DandvatEntry | undefined> {
        const entries = await DandvatService.getAllEntries(userId);
        return entries.find((e) => e.date === dateStr);
    },

    async saveEntry(userId: string, dateStr: string, count: number): Promise<DandvatEntry> {
        const all = await DandvatService._getRawEntries();
        const idx = all.findIndex((e) => e.userId === userId && e.date === dateStr);

        const marks = Number((count / 3).toFixed(2));

        const entry: DandvatEntry = {
            id: idx >= 0 ? all[idx].id : Date.now().toString(),
            userId,
            date: dateStr,
            count,
            marks,
        };

        if (idx >= 0) all[idx] = entry;
        else all.push(entry);

        await DandvatService._saveRawEntries(all);
        return entry;
    },

    async getCurrentMonthSummary(userId: string): Promise<{ totalCount: number; totalMarks: number }> {
        const entries = await DandvatService.getAllEntries(userId);
        const today = new Date();

        const monthEntries = entries.filter((e) => isSameMonth(parseISO(e.date), today));
        const totalCount = monthEntries.reduce((sum, e) => sum + e.count, 0);
        const totalMarks = monthEntries.reduce((sum, e) => sum + e.marks, 0);

        return { totalCount, totalMarks: Number(totalMarks.toFixed(2)) };
    },

    async getFilteredHistory(
        userId: string,
        filterType: HistoryFilter,
        customRange?: { start: Date; end: Date }
    ): Promise<DandvatEntry[]> {
        const entries = await DandvatService.getAllEntries(userId);
        const today = new Date();

        let start: Date | null = null;
        let end: Date | null = null;

        switch (filterType) {
            case "Weekly":
                start = startOfWeek(today);
                end = endOfWeek(today);
                break;
            case "Monthly":
                start = startOfMonth(today);
                end = endOfMonth(today);
                break;
            case "Quarterly":
                start = startOfQuarter(today);
                end = endOfQuarter(today);
                break;
            case "Yearly":
                start = startOfYear(today);
                end = endOfYear(today);
                break;
            case "Custom":
                start = customRange?.start ?? null;
                end = customRange?.end ?? null;
                break;
        }

        let filtered = entries;
        if (start && end) {
            filtered = entries.filter((e) =>
                isWithinInterval(parseISO(e.date), { start, end })
            );
        }

        return filtered.sort((a, b) => (a.date < b.date ? 1 : -1));
    },

    todayStr(): string {
        return format(new Date(), "yyyy-MM-dd");
    },
};
